import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useTheme, alpha } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MDButton from "components/MDButton";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useApi } from "lib/api";


function parseNumeroLoose(val) {
  const t = String(val ?? "")
    .trim()
    .replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && !Number.isNaN(n) ? n : null;
}

/** Monto para celdas; evita ruido de coma flotante. */
function formatMonto(n) {
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 100) / 100);
}

function normalizeRow(r) {
  if (!r || typeof r !== "object") {
    return {
      chasis: "",
      marca: "",
      modelo: "",
      color: "",
      precio: "",
      precioMinimo: "",
      costo: "",
      precioBase: 0,
      costoBase: 0,
      sucursal: "",
    };
  }
  const precioStr =
    r.precio != null ? String(r.precio) : r.price != null ? String(r.price) : "";
  const costoStr = r.costo != null ? String(r.costo) : r.cost != null ? String(r.cost) : "";
  const precioMinimoRaw =
    r.mprecio != null
      ? String(r.mprecio)
      : r.precioMinimo != null
        ? String(r.precioMinimo)
        : "";
  const precioMinimoStr = precioMinimoRaw.trim() !== "" ? precioMinimoRaw : precioStr;
  const precioBase = parseNumeroLoose(precioStr) ?? 0;
  const costoBase = parseNumeroLoose(costoStr) ?? 0;
  return {
    chasis: String(r.chasis ?? r.chassis ?? ""),
    marca: String(r.marca ?? r.brand ?? ""),
    modelo: String(r.modelo ?? r.model ?? ""),
    color: String(r.color ?? ""),
    precio: precioStr,
    precioMinimo: precioMinimoStr,
    costo: costoStr,
    precioBase,
    costoBase,
    sucursal: String(r.sucursal ?? ""),
  };
}

function esNumeroValido(val) {
  const t = String(val ?? "")
    .trim()
    .replace(",", ".");
  if (t === "") return false;
  const n = Number(t);
  return Number.isFinite(n) && !Number.isNaN(n);
}

function parseNumero(val) {
  const t = String(val ?? "")
    .trim()
    .replace(",", ".");
  return Number(t);
}

/** Fila válida para guardar: precio, precio mín., costo, marca y modelo obligatorios; también chasis y color. */
function filaListaParaGuardar(row) {
  const t = (v) => String(v ?? "").trim();
  return (
    t(row.precio) !== "" &&
    esNumeroValido(row.precioMinimo) &&
    t(row.costo) !== "" &&
    t(row.marca) !== "" &&
    t(row.modelo) !== "" &&
    t(row.chasis) !== "" &&
    t(row.color) !== "" &&
    t(row.sucursal) !== ""
  );
}

const inventarioBulkColumns = [
  { field: "chasis", headerName: "Chasis", editable: true, flex: 1.2, minWidth: 140 },
  { field: "marca", headerName: "Marca", editable: true, flex: 0.7, minWidth: 90 },
  { field: "modelo", headerName: "Modelo", editable: true, flex: 1, minWidth: 120 },
  { field: "color", headerName: "Color", editable: true, flex: 0.6, minWidth: 80 },
  { field: "precio", headerName: "Precio", editable: true, flex: 0.8, minWidth: 100 },
  {
    field: "precioMinimo",
    headerName: "Precio mín",
    editable: true,
    flex: 0.75,
    minWidth: 100,
  },
  { field: "costo", headerName: "Costo", editable: true, flex: 0.8, minWidth: 100 },
];

function SectionHeader({ icon: Icon, title, subtitle }) {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: 1,
        borderColor: "divider",
        bgcolor:
          theme.palette.mode === "dark"
            ? alpha(theme.palette.primary.main, 0.12)
            : theme.palette.grey[50],
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor:
            theme.palette.mode === "dark"
              ? alpha(theme.palette.primary.main, 0.2)
              : alpha(theme.palette.primary.main, 0.12),
          color: "primary.main",
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box>
        <Typography variant="subtitle2" fontWeight={600} color="text.primary">
          {title}
        </Typography>
        {subtitle != null && subtitle !== "" && (
          <Typography variant="caption" color="text.secondary" component="div">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function InventariobulkModal({ open, onClose }) {
  const { post } = useApi();
  const theme = useTheme();
  /** @type {[{ id: string, file: File, previewUrl: string }]} */
  const [imagenes, setImagenes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [step, setStep] = useState(1);
  const [formFactura, setFormFactura] = useState({
    suplidor: "",
    suplidorId: "",
    numFactura: "",
    fecha: "",
    tasa: "",
    concepto: "",
    consignado: false,
  });
  const [sucursalOptions, setSucursalOptions] = useState([]);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const inputRef = useRef(null);

  const resetState = () => {
    setImagenes((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      return [];
    });
    setSelectedId(null);
    setError(null);
    setResult(null);
    setRows([]);
    setStep(1);
    setFormFactura({
      suplidor: "",
      suplidorId: "",
      numFactura: "",
      fecha: "",
      tasa: "",
      concepto: "",
      consignado: false,
    });
    setSucursalOptions([]);
    setLoading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const onPickFile = (e) => {
    const list = e.target.files ? Array.from(e.target.files) : [];
    setError(null);
    setResult(null);
    setRows([]);
    setStep(1);
    if (list.length === 0) return;
    const valid = list.filter((f) => f.type.startsWith("image/"));
    if (valid.length === 0) {
      setError("Seleccione archivos de imagen.");
      e.target.value = "";
      return;
    }
    if (valid.length < list.length) {
      setError("Algunos archivos no son imagen; se omitieron.");
    }
    const nuevas = valid.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setImagenes((prev) => [...prev, ...nuevas]);
    setSelectedId(nuevas[nuevas.length - 1].id);
    e.target.value = "";
  };

  const removeImagen = (id) => {
    setImagenes((prev) => {
      const hit = prev.find((x) => x.id === id);
      if (!hit) return prev;
      URL.revokeObjectURL(hit.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
    setResult(null);
    setRows([]);
    setStep(1);
  };

  const limpiarImagenes = () => {
    setImagenes((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      return [];
    });
    setSelectedId(null);
    setResult(null);
    setRows([]);
    setStep(1);
  };

  useEffect(() => {
    if (imagenes.length === 0) {
      setSelectedId(null);
      return;
    }
    setSelectedId((sid) =>
      sid != null && imagenes.some((x) => x.id === sid) ? sid : imagenes[0].id
    );
  }, [imagenes]);

  useEffect(() => {
    if (!open) return;
    post("/api/inventariobulk/suppliers")
      .then(({ ok, data }) => {
        const options = ok && Array.isArray(data?.suppliers)
          ? data.suppliers
              .filter((s) => s && s.label)
              .map((s) => ({ id: String(s.id ?? ""), label: String(s.label) }))
          : [];
        setSupplierOptions(options);
      })
      .catch(() => {
        setSupplierOptions([]);
      });
  }, [open, post]);

  useEffect(() => {
    if (!open) return;
    post("/api/recuperasucursales")
      .then(({ ok, data }) => {
        const list = ok && Array.isArray(data?.sucursales) ? data.sucursales : [];
        setSucursalOptions(list);
      })
      .catch(() => {
        setSucursalOptions([]);
      });
  }, [open, post]);

  const defaultSucursalLabel = useMemo(() => {
    const raw = String(localStorage.getItem("sucursal") || "").trim();
    if (!raw) return "";
    const exact = sucursalOptions.find((o) => String(o?.label ?? "") === raw);
    if (exact) return String(exact.label);
    const prefix = raw.split("-")[0].trim();
    if (prefix) {
      const byPrefix = sucursalOptions.find(
        (o) => String(o?.label ?? "").split("-")[0].trim() === prefix
      );
      if (byPrefix) return String(byPrefix.label);
    }
    return raw;
  }, [sucursalOptions]);

  useEffect(() => {
    if (!defaultSucursalLabel) return;
    setRows((prev) => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((r) => {
        if (!String(r.sucursal ?? "").trim()) {
          changed = true;
          return { ...r, sucursal: defaultSucursalLabel };
        }
        return r;
      });
      return changed ? next : prev;
    });
  }, [defaultSucursalLabel]);

  const handleSubmit = async () => {
    if (imagenes.length === 0) {
      setError("Seleccione al menos una imagen.");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setRows([]);
    setStep(1);
    const combined = [];
    try {
      for (let i = 0; i < imagenes.length; i++) {
        const fd = new FormData();
        fd.append("image", imagenes[i].file);
        const res = await fetch(
          process.env.REACT_APP_SERVIDOR + "/api/extract-motorcycles",
          { method: "POST", body: fd }
        );
        if (!res.ok) {
          const t = await res.text();
          throw new Error(t || res.statusText || `Error imagen ${i + 1}`);
        }
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        combined.push(...items);
      }
      setResult({ items: combined });
      setRows(
        combined.map((r, i) => ({
          id: i,
          ...normalizeRow(r),
          sucursal: defaultSucursalLabel,
        }))
      );
    } catch (err) {
      setError(err.message || "Error al procesar las imágenes.");
    } finally {
      setLoading(false);
    }
  };

  const processRowUpdate = useCallback(
    (newRow, oldRow) => {
      const tasaVal = esNumeroValido(formFactura.tasa) ? parseNumero(formFactura.tasa) : null;
      let precioBase = newRow.precioBase ?? oldRow?.precioBase ?? 0;
      let costoBase = newRow.costoBase ?? oldRow?.costoBase ?? 0;

      if (newRow.precio !== oldRow?.precio) {
        const p = parseNumeroLoose(newRow.precio);
        const pv = p ?? 0;
        precioBase = pv;
      }
      if (newRow.costo !== oldRow?.costo) {
        const c = parseNumeroLoose(newRow.costo);
        const cv = c ?? 0;
        costoBase = tasaVal != null && tasaVal !== 0 ? cv / tasaVal : cv;
      }

      const out = {
        ...newRow,
        precioBase,
        costoBase,
      };
      if (tasaVal != null && tasaVal !== 0) {
        out.precio = formatMonto(precioBase);
        out.costo = formatMonto(costoBase * tasaVal);
      } else {
        out.precio = formatMonto(precioBase);
        out.costo = formatMonto(costoBase);
      }
      setRows((prev) => prev.map((row) => (row.id === newRow.id ? out : row)));
      return out;
    },
    [formFactura.tasa]
  );

  const inventarioBulkGridColumns = useMemo(
    () => [
      ...inventarioBulkColumns,
      {
        field: "sucursal",
        headerName: "Sucursal",
        type: "singleSelect",
        editable: true,
        valueOptions: sucursalOptions.map((o) => ({
          value: String(o.label),
          label: String(o.label),
        })),
        flex: 1.15,
        minWidth: 200,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 52,
        align: "right",
        headerAlign: "right",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        getActions: (params) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteOutlineIcon fontSize="small" />}
            label="Eliminar fila"
            onClick={() => {
              setRows((prev) => prev.filter((row) => row.id !== params.id));
            }}
          />,
        ],
      },
    ],
    [sucursalOptions]
  );

  /** Suma de la columna Costo de todas las filas (valor total del recibo). */
  const valorTotalSumaCostos = useMemo(() => {
    let s = 0;
    for (const row of rows) {
      const c = parseNumeroLoose(row.costo);
      if (c != null && Number.isFinite(c)) s += c;
    }
    return s;
  }, [rows]);

  const canGuardarPaso2 = useMemo(() => {
    if (step !== 2 || rows.length === 0) return false;
    const f = formFactura;
    const formOk =
      f.suplidor.trim() !== "" &&
      f.numFactura.trim() !== "" &&
      f.fecha.trim() !== "" &&
      esNumeroValido(f.tasa) &&
      f.concepto.trim() !== "";
    if (!formOk) return false;
    return rows.every(filaListaParaGuardar);
  }, [step, rows, formFactura]);

  const imagenVista = useMemo(
    () => imagenes.find((x) => x.id === selectedId) ?? imagenes[0] ?? null,
    [imagenes, selectedId]
  );

  const handleGuardar = () => {
    if (!canGuardarPaso2 || loading) return;
    if (rows.some((row) => !filaListaParaGuardar(row))) return;
    const payload = {
      factura: {
        ...formFactura,
        suplidor_id: formFactura.suplidorId,
        tasa: parseNumero(formFactura.tasa),
        valorTotal: valorTotalSumaCostos,
        consignado: formFactura.consignado ? "S" : "N",
      },
      items: rows.map(({ id, precioBase, costoBase, precioMinimo, ...rest }) => ({
        ...rest,
        mprecio: parseNumero(precioMinimo),
      })),
    };
    setLoading(true);
    post("/api/inventariobulk/save-receipt", {
      factura: payload.factura,
      items: payload.items,
    })
      .then(({ ok, data }) => {
        if (!ok) {
          throw new Error(data?.Error || "");
        }
        setSaveSuccessOpen(true);
        handleClose();
      })
      .catch((err) => {
        setError(err.message || "No se pudo guardar el recibo");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const gridHeight = { xs: "38vh", md: "calc(100vh - 320px)" };
  const imageMinHeight = { md: "calc(100vh - 320px)" };

  return (
    <>
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      fullScreen
      PaperProps={{
        sx: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: theme.palette.background.default,
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          background:
            "linear-gradient(135deg, #0d47a1 0%, #1565c0 38%, #1976d2 72%, #2196f3 100%)",
          color: theme.palette.common.white,
          boxShadow: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha("#fff", 0.15),
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 28, color: theme.palette.common.white }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={600}
              letterSpacing={-0.3}
              sx={(t) => ({
                color: t.palette.common.white,
                "&.MuiTypography-h5": { color: t.palette.common.white },
              })}
            >
              Inventario bulk
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 0.25, color: alpha(theme.palette.common.white, 0.92) }}
            >
              {step === 1
                ? "Paso 1: imagen y extracción. Luego continúe para completar la factura."
                : "Paso 2: revise las filas y complete los datos de factura / suplidor."}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          aria-label="cerrar"
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: "inherit",
            bgcolor: alpha("#fff", 0.1),
            "&:hover": { bgcolor: alpha("#fff", 0.2) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {loading && (
        <LinearProgress
          color="inherit"
          sx={{
            height: 3,
            bgcolor: alpha("#0d47a1", 0.25),
            "& .MuiLinearProgress-bar": {
              transition: "transform 0.4s ease",
              background: "linear-gradient(90deg, #42a5f5, #90caf9)",
            },
          }}
        />
      )}

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          pt: 3,
          px: { xs: 2, sm: 3 },
          pb: 2,
          bgcolor: theme.palette.background.default,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={onPickFile}
        />

        {step === 1 && (
          <Paper
          elevation={0}
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            borderColor: alpha(theme.palette.divider, 0.9),
            bgcolor: "background.paper",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <MDButton
                variant="gradient"
                color="info"
                size="small"
                onClick={() => inputRef.current?.click()}
                disabled={loading}
                startIcon={<AddPhotoAlternateOutlinedIcon />}
              >
                Elegir imágenes
              </MDButton>
              {imagenes.length > 0 && (
                <Chip
                  label={`${imagenes.length} imagen${imagenes.length === 1 ? "" : "es"}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {imagenes.length > 0 && (
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  onClick={limpiarImagenes}
                  disabled={loading}
                >
                  Quitar todas
                </MDButton>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420 }}>
              Puede seleccionar varias imágenes (JPG, PNG, etc.). Se combinarán las filas extraídas
              de cada una al pulsar «Extraer con IA».
            </Typography>
          </Stack>
        </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={22} thickness={4} />
            <Typography variant="body2" color="text.secondary">
              Analizando {imagenes.length} imagen{imagenes.length === 1 ? "" : "es"}…
            </Typography>
          </Stack>
        )}

        {step === 1 && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              flex: { xs: "none", md: "1 1 30%" },
              width: { xs: "100%", md: "auto" },
              minWidth: 0,
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
            }}
          >
            <SectionHeader
              icon={ImageOutlinedIcon}
              title="Imágenes"
              subtitle={
                imagenes.length > 0
                  ? `${imagenes.length} archivo${imagenes.length === 1 ? "" : "s"} · Pulse una miniatura para ampliar`
                  : "Añada una o más fotos del listado"
              }
            />
            <Box
              sx={{
                flex: 1,
                minHeight: { xs: 260, md: 0 },
                maxHeight: { xs: "42vh", md: "none" },
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                justifyContent: "flex-start",
                gap: 1.5,
              }}
            >
              {imagenes.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ overflowX: "auto", pb: 0.5, flexShrink: 0 }}>
                  {imagenes.map((img) => (
                    <Box
                      key={img.id}
                      sx={{
                        position: "relative",
                        flexShrink: 0,
                        width: 84,
                      }}
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setSelectedId(img.id)}
                        sx={{
                          p: 0,
                          m: 0,
                          display: "block",
                          border: 2,
                          borderColor:
                            (selectedId === img.id ||
                              (selectedId == null && imagenes[0]?.id === img.id))
                              ? "primary.main"
                              : "divider",
                          borderRadius: 1,
                          overflow: "hidden",
                          cursor: "pointer",
                          bgcolor: "background.paper",
                          width: "100%",
                        }}
                      >
                        <Box
                          component="img"
                          src={img.previewUrl}
                          alt=""
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        aria-label="Quitar imagen"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          removeImagen(img.id);
                        }}
                        sx={{
                          position: "absolute",
                          top: -10,
                          right: -10,
                          bgcolor: "background.paper",
                          boxShadow: 1,
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              )}
              {imagenVista ? (
                <Box
                  sx={{
                    width: "100%",
                    flex: 1,
                    minHeight: { xs: 220, ...imageMinHeight },
                    maxHeight: { xs: "42vh", md: "none" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 1.5,
                    overflow: "auto",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Box
                    component="img"
                    src={imagenVista.previewUrl}
                    alt={imagenVista.file.name}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      width: "auto",
                      height: "auto",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{
                    width: "100%",
                    minHeight: imageMinHeight,
                    maxHeight: { xs: "42vh", md: "none" },
                    borderRadius: 1.5,
                    border: 2,
                    borderStyle: "dashed",
                    borderColor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.main, 0.5)
                        : theme.palette.grey[300],
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.main, 0.08)
                        : theme.palette.grey[50],
                    py: 4,
                    px: 2,
                  }}
                >
                  <ImageOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", opacity: 0.7 }} />
                  <Typography variant="body2" color="text.secondary" align="center">
                    Aún no hay imágenes
                  </Typography>
                  <Typography variant="caption" color="text.disabled" align="center">
                    Use «Elegir imágenes» para cargar archivos
                  </Typography>
                </Stack>
              )}
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                px: 2,
                py: 2,
                borderTop: 1,
                borderColor: "divider",
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha(theme.palette.grey[500], 0.08)
                    : theme.palette.grey[50],
              }}
            >
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                onClick={handleSubmit}
                disabled={loading || imagenes.length === 0}
              >
                Extraer con IA
              </MDButton>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              flex: { xs: "none", md: "1 1 52%" },
              width: { xs: "100%", md: "auto" },
              minHeight: { xs: 200, md: 0 },
              minWidth: 0,
              borderRadius: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
            }}
          >
            <SectionHeader
              icon={GridOnOutlinedIcon}
              title="Datos extraídos"
              subtitle={
                <>
                  <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                    {rows.length}
                  </Box>
                  {" chasis registrados · Doble clic en una celda para editar"}
                </>
              }
            />
            {rows.length > 0 ? (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  height: gridHeight,
                  width: "100%",
                  p: 1,
                  "& .MuiDataGrid-root": {
                    border: "none",
                    borderRadius: 1,
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                    borderRadius: "8px 8px 0 0",
                  },
                  "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, fontSize: "0.8125rem" },
                  "& .MuiDataGrid-cell": { fontSize: "0.8125rem" },
                }}
              >
                <DataGrid
                  rows={rows}
                  columns={inventarioBulkGridColumns}
                  processRowUpdate={processRowUpdate}
                  disableRowSelectionOnClick
                  hideFooter
                  density="compact"
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-cell:focus-within": { outline: "none" },
                  }}
                />
              </Box>
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{
                  flex: 1,
                  minHeight: gridHeight,
                  px: 2,
                  py: 3,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[900]
                      : theme.palette.grey[50],
                }}
              >
                <GridOnOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                <Typography color="text.secondary" align="center" variant="body2">
                  {result && rows.length === 0 && !loading
                    ? "No se extrajeron filas en esta imagen."
                    : "Los datos aparecerán aquí después de enviar la imagen."}
                </Typography>
              </Stack>
            )}
          </Paper>
        </Box>
        )}

        {step === 2 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: "stretch",
            }}
          >
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                flex: { xs: "none", md: "1 1 52%" },
                width: { xs: "100%", md: "auto" },
                minHeight: { xs: 200, md: 0 },
                minWidth: 0,
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
              }}
            >
              <SectionHeader
                icon={GridOnOutlinedIcon}
                title="Datos extraídos"
                subtitle={
                  <>
                    <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                      {rows.length} chasis registrados
                    </Box>
                    {" · Sucursal por fila · Doble clic en una celda para editar"}
                  </>
                }
              />
              {rows.length > 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    height: gridHeight,
                    width: "100%",
                    p: 1,
                    "& .MuiDataGrid-root": {
                      border: "none",
                      borderRadius: 1,
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? theme.palette.grey[800]
                          : theme.palette.grey[100],
                      borderRadius: "8px 8px 0 0",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, fontSize: "0.8125rem" },
                    "& .MuiDataGrid-cell": { fontSize: "0.8125rem" },
                  }}
                >
                  <DataGrid
                    rows={rows}
                    columns={inventarioBulkGridColumns}
                    processRowUpdate={processRowUpdate}
                    disableRowSelectionOnClick
                    hideFooter
                    density="compact"
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-cell:focus-within": { outline: "none" },
                    }}
                  />
                </Box>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{
                    flex: 1,
                    minHeight: gridHeight,
                    px: 2,
                    py: 3,
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[900]
                        : theme.palette.grey[50],
                  }}
                >
                  <Typography color="text.secondary" align="center" variant="body2">
                    No hay filas. Vuelva al paso 1 para extraer datos.
                  </Typography>
                </Stack>
              )}
            </Paper>

            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                flex: { xs: "none", md: "1 1 48%" },
                width: { xs: "100%", md: "auto" },
                minWidth: 0,
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.paper",
              }}
            >
              <SectionHeader
                icon={ReceiptLongOutlinedIcon}
                title="Factura y suplidor"
                subtitle="Complete los campos antes de registrar"
              />
              <Stack spacing={2} sx={{ p: 2, flex: 1, overflow: "auto" }}>
                <Autocomplete
                  freeSolo
                  options={supplierOptions}
                  value={formFactura.suplidor}
                  onChange={(event, newValue) => {
                    if (newValue && typeof newValue === "object") {
                      setFormFactura((p) => ({
                        ...p,
                        suplidor: String(newValue.label ?? ""),
                        suplidorId: String(newValue.id ?? ""),
                      }));
                      return;
                    }
                    setFormFactura((p) => ({
                      ...p,
                      suplidor: newValue != null ? String(newValue) : "",
                      suplidorId: "",
                    }));
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input" || reason === "clear") {
                      setFormFactura((p) => ({
                        ...p,
                        suplidor: newInputValue,
                        suplidorId: "",
                      }));
                    }
                  }}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option?.label ?? ""
                  }
                  isOptionEqualToValue={(option, value) => {
                    const optionId = typeof option === "string" ? option : option?.id;
                    const valueId = typeof value === "string" ? value : value?.id;
                    return String(optionId ?? "") === String(valueId ?? "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Suplidor"
                      size="small"
                      variant="outlined"
                      placeholder="Buscar o escribir…"
                    />
                  )}
                />
                <TextField
                  label="# FACTURA"
                  value={formFactura.numFactura}
                  onChange={(e) =>
                    setFormFactura((p) => ({ ...p, numFactura: e.target.value }))
                  }
                  fullWidth
                  size="small"
                  variant="outlined"
                />
                <TextField
                  label="Fecha"
                  type="date"
                  value={formFactura.fecha}
                  onChange={(e) =>
                    setFormFactura((p) => ({ ...p, fecha: e.target.value }))
                  }
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Tasa"
                  type="number"
                  value={formFactura.tasa}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormFactura((p) => ({ ...p, tasa: v }));
                    const t = esNumeroValido(v) ? parseNumero(v) : null;
                    setRows((prev) =>
                      prev.map((row) => {
                        const pb = row.precioBase ?? parseNumeroLoose(row.precio) ?? 0;
                        const cb = row.costoBase ?? parseNumeroLoose(row.costo) ?? 0;
                        if (t != null && t !== 0) {
                          return {
                            ...row,
                            precioBase: pb,
                            costoBase: cb,
                            precio: formatMonto(pb),
                            costo: formatMonto(cb * t),
                          };
                        }
                        return {
                          ...row,
                          precioBase: pb,
                          costoBase: cb,
                          precio: formatMonto(pb),
                          costo: formatMonto(cb),
                        };
                      })
                    );
                  }}
                  fullWidth
                  size="small"
                  variant="outlined"
                  inputProps={{
                    step: "any",
                    min: 0,
                    inputMode: "decimal",
                  }}
                />
                <TextField
                  label="Valor total (suma de costos)"
                  value={formatMonto(valorTotalSumaCostos)}
                  fullWidth
                  size="small"
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  helperText="Calculado automáticamente"
                />
                <TextField
                  label="Concepto"
                  value={formFactura.concepto}
                  onChange={(e) =>
                    setFormFactura((p) => ({ ...p, concepto: e.target.value }))
                  }
                  fullWidth
                  size="small"
                  variant="outlined"
                  multiline
                  minRows={3}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!formFactura.consignado}
                      onChange={(e) =>
                        setFormFactura((p) => ({ ...p, consignado: e.target.checked }))
                      }
                      color="info"
                    />
                  }
                  label={formFactura.consignado ? "Consignado" : "No consignado"}
                />
              </Stack>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          gap: 1.5,
        }}
      >
        {step === 2 && (
          <MDButton variant="outlined" color="dark" onClick={() => setStep(1)} disabled={loading}>
            Atrás
          </MDButton>
        )}
        <MDButton variant="outlined" color="dark" onClick={handleClose} disabled={loading}>
          Cerrar
        </MDButton>
        <Box sx={{ flex: 1 }} />
        {step === 1 && rows.length > 0 && (
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => setStep(2)}
            disabled={loading}
          >
            Continuar
          </MDButton>
        )}
        {step === 2 && (
          <MDButton
            variant="gradient"
            color="success"
            onClick={handleGuardar}
            disabled={loading || !canGuardarPaso2}
          >
            Guardar
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
    <Snackbar
      open={saveSuccessOpen}
      autoHideDuration={6500}
      onClose={(_, reason) => {
        if (reason === "clickaway") return;
        setSaveSuccessOpen(false);
      }}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        onClose={() => setSaveSuccessOpen(false)}
        severity="success"
        variant="filled"
        sx={{
          minWidth: { xs: "100%", sm: 380 },
          alignItems: "center",
          fontWeight: 500,
          borderRadius: 2,
          boxShadow: theme.shadows[8],
          color: theme.palette.common.white,
          "& .MuiAlert-message": { color: theme.palette.common.white },
          "& .MuiAlert-icon": { color: theme.palette.common.white },
          "& .MuiAlert-action .MuiIconButton-root": { color: theme.palette.common.white },
        }}
      >
        Registro guardado correctamente.
      </Alert>
    </Snackbar>
    </>
  );
}

export default InventariobulkModal;
